import { MessageBarType } from "office-ui-fabric-react";
import React, { Component, Fragment, ReactElement } from "react";
import { connect } from "react-redux";
import Actions from "../../redux/Actions";
import { IRootState } from "../../store";
import DispatchProps from "../../utils/DispatchProps";
import ApiService from "../ApiService";
import Table, { TableColumn } from "../Table";
import TableCell from "../TableCell";
import { TableRowProps } from "../TableRow";
import { MailroomAutomationHistoryRecord } from "../../models/MailroomAutomationHistoryRecord";
import BoolPill from "../BoolPill";

interface MailroomAutomationHistoryProps {
    tenantId: string;
    profileId?: string;

    apiService: ApiService;
}

interface MailroomAutomationHistoryState {
    history: MailroomAutomationHistoryRecord[] | null | undefined;
}

class MailroomAutomationHistory extends Component<MailroomAutomationHistoryProps & DispatchProps, MailroomAutomationHistoryState>
{
    constructor(props: MailroomAutomationHistoryProps & DispatchProps) {
        super(props);

        this.state = {
            history: undefined
        };
    }

    public async componentDidMount() {
        if (this.props.tenantId && this.props.profileId) {
            await this.load();
        }
    }

    async componentDidUpdate(prevProps: Readonly<MailroomAutomationHistoryProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.props.tenantId !== prevProps.tenantId || this.props.profileId !== prevProps.profileId) {
            await this.load();
        }
    }

    private columns: TableColumn[] = [
        { title: "Date", type: "date", propertyPath: "dateTime", flex: 1 }, 
        { title: "Processor Item Changes", propertyPath: "stats.processorTotalChangeCount", flex: 1 },
        { title: "CSVs Match Last Upload", propertyPath: "stats.uploadSnapshotMatchesPrevious", flex: 1 },
        { title: "All MIS Data Match Last Upload", propertyPath: "stats.misDataMatchesPrevious", flex: 1 },
        { title: "Total Processor Items", propertyPath: "stats.processorTotalItemCount", flex: 1 },
        { title: "Succeeded Processor Items", propertyPath: "stats.processorTotalSucceededItemCount", flex: 1 },
        { title: "Errored Processor Items", propertyPath: "stats.processorTotalErroredItemCount", flex: 1 },
        { title: "School Codes", propertyPath: "schools", flex: 2 },
        { title: "Status", propertyPath: "error", flex: 1 },
    ];

    private async load() {
        if (this.props.profileId !== undefined) {
            try {
                this.setState({ history: undefined });
                let history = await this.props.apiService.getMailroomAutomationHistory(this.props.tenantId, this.props.profileId);
                this.setState({ history: history });
            }
            catch (e) {
                this.setState({ history: null });
                console.log(e);
                this.props.dispatch(Actions.showMessageBar(`Error loading automation history`, MessageBarType.error));
            }
        }
    }

    private columnRowProps = (row: MailroomAutomationHistoryRecord): TableRowProps => {
        return {
            enableRowLink: false,
        };
    };

    private cellForColumn = (item: MailroomAutomationHistoryRecord, column: TableColumn, columnIndex: number): ReactElement<TableCell> | undefined => {

        switch (column.propertyPath) {
            case "error":
                return <TableCell key={columnIndex} flex={column.flex}>
                    <BoolPill value={item.error}
                        trueText="Error"
                        trueColour="red"
                        falseText="Completed"
                        falseColour="green" />
                    <span className="cdb-table__extra-detail cdb-table__extra-detail--ellipsis"
                        title={item.message}>
                        {item.message}
                    </span>
                </TableCell>;


            case "schools":
                return <TableCell key={columnIndex} flex={column.flex}>
                    <span className="cdb-table__cell">
                        {item.schools?.map(s => s.schoolCode).join(", ")}
                    </span>
                </TableCell>;

            default:
                return undefined;
        }
    };

    public render() {
        switch (this.state.history) {
            case null:
                return <div>Error loading automation history</div>
            default:
                return <Fragment>
                    <div className="flex-row">
                        <div className="flex-item--flex-1">
                            <Table
                                title="Automation History"
                                items={this.state.history == undefined ? [] : this.state.history}
                                loading={this.state.history == undefined}
                                emptyMessage="No instance history"
                                columns={this.columns}
                                rowPropsForItem={this.columnRowProps}
                                cellForColumn={this.cellForColumn}
                                enableSort={true}>
                                {this.props.children}
                            </Table>
                        </div>
                    </div>
                </Fragment>
        }
    }
}

export default connect(
    (state: IRootState, ownProps: { tenantId: string, profileId?: string }): MailroomAutomationHistoryProps => {
        return {
            tenantId: ownProps.tenantId,
            profileId: ownProps.profileId,
            apiService: state.auth.apiService
        };
    }
)(MailroomAutomationHistory);